// ═══════════════════════════════════════════════════════
// WEB VITALS MONITORING — Core Web Vitals
// Collects LCP, FID, CLS, TTFB via PerformanceObserver
// Sends to Sentry + logs to system_metrics table
// ═══════════════════════════════════════════════════════
import { useEffect } from "react";
import { supabase } from "../services/core.js";

interface WebVitalMetric {
  name: "LCP" | "FID" | "CLS" | "TTFB";
  value: number;
  timestamp: number;
  label?: string;
}

/**
 * useWebVitals — Collect Core Web Vitals and report to Sentry + Supabase
 * Call once in App.jsx or ClientLayout (after auth context is ready)
 * Only runs in production (checks import.meta.env.PROD)
 */
export function useWebVitals() {
  useEffect(() => {
    // Only run in production
    if (!import.meta.env.PROD) return;

    const metrics: Partial<Record<string, WebVitalMetric>> = {};

    // ── LCP: Largest Contentful Paint ────────────────────────
    const lcpObserver = new PerformanceObserver((list) => {
      try {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcpValue = lastEntry.renderTime || lastEntry.loadTime;

        metrics["lcp"] = {
          name: "LCP",
          value: lcpValue,
          timestamp: Date.now(),
          label: "Largest Contentful Paint (ms)",
        };

        // Send to Sentry if available
        if (window.__SENTRY__) {
          window.__SENTRY__.captureMessage(`LCP: ${lcpValue.toFixed(0)}ms`, "info", {
            tags: { metric_type: "lcp" },
          });
        }

        logMetricToSupabase(metrics["lcp"]);
      } catch (err) {
        console.warn("[useWebVitals] LCP collection failed:", err);
      }
    });

    try {
      lcpObserver.observe({ entryTypes: ["largest-contentful-paint"], buffered: true });
    } catch (err) {
      console.warn("[useWebVitals] LCP observer not supported:", err);
    }

    // ── FID: First Input Delay ──────────────────────────────
    const fidObserver = new PerformanceObserver((list) => {
      try {
        const entries = list.getEntries();
        const firstEntry = entries[0];
        const fidValue = firstEntry.processingDuration;

        metrics["fid"] = {
          name: "FID",
          value: fidValue,
          timestamp: Date.now(),
          label: "First Input Delay (ms)",
        };

        if (window.__SENTRY__) {
          window.__SENTRY__.captureMessage(`FID: ${fidValue.toFixed(0)}ms`, "info", {
            tags: { metric_type: "fid" },
          });
        }

        logMetricToSupabase(metrics["fid"]);
      } catch (err) {
        console.warn("[useWebVitals] FID collection failed:", err);
      }
    });

    try {
      fidObserver.observe({ entryTypes: ["first-input"], buffered: true });
    } catch (err) {
      console.warn("[useWebVitals] FID observer not supported:", err);
    }

    // ── CLS: Cumulative Layout Shift ────────────────────────
    const clsObserver = new PerformanceObserver((list) => {
      try {
        let clsValue = 0;
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });

        metrics["cls"] = {
          name: "CLS",
          value: parseFloat(clsValue.toFixed(3)),
          timestamp: Date.now(),
          label: "Cumulative Layout Shift (unitless)",
        };

        if (window.__SENTRY__) {
          window.__SENTRY__.captureMessage(`CLS: ${clsValue.toFixed(3)}`, "info", {
            tags: { metric_type: "cls" },
          });
        }

        logMetricToSupabase(metrics["cls"]);
      } catch (err) {
        console.warn("[useWebVitals] CLS collection failed:", err);
      }
    });

    try {
      clsObserver.observe({ entryTypes: ["layout-shift"], buffered: true });
    } catch (err) {
      console.warn("[useWebVitals] CLS observer not supported:", err);
    }

    // ── TTFB: Time to First Byte ────────────────────────────
    // TTFB is measured from performance.timing (not PerformanceObserver)
    const measureTTFB = () => {
      try {
        const timing = performance.timing;
        if (timing.navigationStart && timing.responseStart) {
          const ttfbValue = timing.responseStart - timing.navigationStart;

          metrics["ttfb"] = {
            name: "TTFB",
            value: ttfbValue,
            timestamp: Date.now(),
            label: "Time to First Byte (ms)",
          };

          if (window.__SENTRY__) {
            window.__SENTRY__.captureMessage(`TTFB: ${ttfbValue.toFixed(0)}ms`, "info", {
              tags: { metric_type: "ttfb" },
            });
          }

          logMetricToSupabase(metrics["ttfb"]);
        }
      } catch (err) {
        console.warn("[useWebVitals] TTFB collection failed:", err);
      }
    };

    // Measure TTFB after page load
    if (document.readyState === "complete") {
      measureTTFB();
    } else {
      window.addEventListener("load", measureTTFB, { once: true });
    }

    // ── Cleanup ──────────────────────────────────────────────
    return () => {
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clsObserver.disconnect();
    };
  }, []);
}

/**
 * logMetricToSupabase — Fire-and-forget log to system_metrics table
 * Non-blocking: failures are silently logged to console
 */
async function logMetricToSupabase(metric: WebVitalMetric) {
  try {
    // Insert into system_metrics table (no auth required, RLS allows anonymous)
    const { error } = await supabase.from("system_metrics").insert({
      metric_name: metric.name,
      metric_value: metric.value,
      metric_label: metric.label,
      collected_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
    });

    if (error) {
      console.warn(`[useWebVitals] Failed to log ${metric.name}:`, error.message);
    }
  } catch (err) {
    console.warn("[useWebVitals] Supabase insert failed:", err);
  }
}

// Type augmentation for Sentry global
declare global {
  interface Window {
    __SENTRY__?: {
      captureMessage: (msg: string, level: string, context?: any) => void;
    };
  }
}
